import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import type { ReactNode } from "react"
import type { DeclarationInquiryState } from "../../../../../api/declarationInquiriesApi"
import type { FrameworkDerivation } from "../mapping/deriveFrameworkVerdict"
import { FrameworkRow } from "./FrameworkRow"

export interface FrameworkNavigatorProps {
  frameworks: string[]
  nameOf: (framework: string) => string
  derivationOf: (framework: string) => FrameworkDerivation | null
  geprueft: (framework: string) => boolean
  activeFramework: string | null
  onSelect: (framework: string) => void
  /** "+ alle konformen als geprüft markieren" (`BulkConformGesture`) — a slot
   *  rather than this component owning the gesture itself, so it stays a
   *  plain presentational list a test can render standalone with no
   *  mutation hooks to mock. Omitted entirely renders the bare border. */
  footer?: ReactNode
  /** Per-framework Rückfrage state — feeds each row's muted dot + mail
   *  glyph. Optional so a test can render this standalone with no inquiry
   *  data at all (every row then reads "none", the ordinary look). */
  inquiryStateOf?: (
    framework: string,
  ) => Exclude<DeclarationInquiryState, "draft">
  /** B1 — every card of this framework is `excluded` (rejected). Feeds each
   *  row's distinct "geprüft, but nothing left to look at" sentence. Optional
   *  so a standalone test/render can omit it (every row then reads `false`,
   *  the ordinary checked sentence). */
  allExcludedOf?: (framework: string) => boolean
  /** Paged mode (C1-d): rows whose server answer is pending or unknown — never
   *  shown or counted as geprüft. Optional; absent = every row trusted. */
  regulationStatusOf?: (framework: string) => "pending" | "unknown" | null
}

/**
 * The left pane — "Verordnungen" — one row per framework the declaration
 * carries. The caller omits this panel entirely below two frameworks (a
 * single-Verordnung declaration has nothing to navigate); this component does
 * not gate on that itself, so a test can render it standalone.
 *
 * Markup follows the prototype's own pane (`.pane` > `.pnh` + `.pbody` +
 * `.pfoot`) — the header's counter is derived from `geprueft`/`frameworks`
 * here rather than threaded in as a new prop, since both are already on hand.
 */
export function FrameworkNavigator({
  frameworks,
  nameOf,
  derivationOf,
  geprueft,
  activeFramework,
  onSelect,
  footer,
  inquiryStateOf = () => "none",
  allExcludedOf = () => false,
  regulationStatusOf = () => null,
}: FrameworkNavigatorProps) {
  const { translate } = useTranslation()
  const label = translate("MC_DECL_V2_NAV_ARIA")
  const checkedCount = frameworks.filter(
    (fw) => geprueft(fw) && regulationStatusOf(fw) === null,
  ).length
  return (
    <div className="sv2-pane">
      <div className="sv2-pnh">
        <span className="sv2-t">{label}</span>
        <span className="sv2-n sv2-ml">
          {checkedCount}/{frameworks.length}
        </span>
      </div>
      <nav aria-label={label} className="sv2-pbody">
        {frameworks.map((fw) => (
          <FrameworkRow
            key={fw}
            framework={fw}
            name={nameOf(fw)}
            derivation={derivationOf(fw)}
            checked={geprueft(fw)}
            active={fw === activeFramework}
            onSelect={() => onSelect(fw)}
            inquiryState={inquiryStateOf(fw)}
            allExcluded={allExcludedOf(fw)}
            regulationStatus={regulationStatusOf(fw)}
          />
        ))}
      </nav>
      {/* Rendered only when a footer is actually passed — the bulk gesture
          moved into the action bar's left slot (StudioV2FooterBar), and an
          unconditional empty `sv2-pfoot` would leave a bare 54px band at
          the pane's foot. The slot stays for standalone renders/tests. */}
      {footer != null && <div className="sv2-pfoot">{footer}</div>}
    </div>
  )
}
